import * as tslib_1 from "tslib";
import { NavigationStart, Router } from '@angular/router';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { toPayload, withPredicateFrom } from '../../core/helpers/rx-pipes';
import { ProductService } from '../services/product-service';
import * as product from '../actions/product';
import { GetFavoritesSuccess } from '../actions/product';
import * as facet from '../actions/facet';
import * as fromCatalog from '../reducers';
import * as fromBase from '../../base/reducers';
import * as fromAuth from '../../auth/reducers';
import chaining from '../../core/helpers/chaining';
var ProductEffects = /** @class */ (function () {
    function ProductEffects(router, actions$, productSvc, store) {
        var _this = this;
        this.router = router;
        this.actions$ = actions$;
        this.productSvc = productSvc;
        this.store = store;
        /**
         * Product Load Worker Effects
         */
        this.loadWhenInStockToggled$ = this.actions$.ofType(product.TOGGLE_INSTOCK)
            .pipe(switchMap(function () { return [new product.Filter(), new facet.Filter()]; }));
        this.filterProductsOnSetProductFilter$ = this.actions$.ofType(product.SET_PRODUCT_FILTER)
            .pipe(switchMap(function () { return [new product.Filter(), new facet.Filter()]; }));
        this.setDefaultCatalogSortOrder$ = this.actions$.pipe(ofType(product.SET_PRODUCT_DEFAULT_SORT_ORDER), withLatestFrom(this.store.pipe(select(fromBase.getTenantSettings)), function (action, tenantSettings) { return ({ action: action, tenantSettings: tenantSettings }); }), filter(function (data) {
            var catalogSortOrders = data.tenantSettings['catalog_sort_orders'];
            return (catalogSortOrders && catalogSortOrders.length > 0);
        }), map(function (data) {
            var defaultSortOrder = data.tenantSettings['catalog_sort_orders'][0].key;
            return new product.SetProductDefaultSortOrderSuccess(defaultSortOrder);
        }));
        this.sortProductsOnSetProductSortOrder$ = this.actions$.ofType(product.SET_PRODUCT_SORT_ORDER)
            .pipe(switchMap(function () { return [new product.Filter()]; }));
        this.filterProducts$ = this.actions$.ofType(product.FILTER)
            .pipe(withLatestFrom(this.store.pipe(select(fromCatalog.getProductFilterables)), this.store.pipe(select(fromBase.getPageParams)), this.store.pipe(select(fromCatalog.getProductSortOrder)), function (action, filterables, params, sortOrder) { return ({ filterables: filterables, params: params, sortOrder: sortOrder }); }), switchMap(function (data) {
            return _this.productSvc
                .findProducts(data.filterables.customerId, data.filterables.pageId, data.filterables.facets, data.filterables.onlyInStock, null, data.params, data.filterables.searchTerm, null, null, data.filterables.filterItems, data.sortOrder)
                .pipe(map(function (products) { return new product.FilterSuccess(products); }), catchError(function (e) { return of(new product.FilterFailed(e)); }));
        }));
        /**
         * Product Expansion Effect
         */
        this.flushDistinctExpand$ = this.router.events
            .pipe(filter(function (ev) { return ev instanceof NavigationStart
            && ev.url.indexOf('/catalog') !== -1; }));
        this.expand$ = this.actions$.ofType(product.EXPAND)
            .pipe(withPredicateFrom(this.store.pipe(select(fromCatalog.canExpand)), function (canExpand) { return canExpand; }), withLatestFrom(this.store.pipe(select(fromCatalog.getCombinedExpandState)), this.store.pipe(select(fromCatalog.getNextPageNumber)), this.store.pipe(select(fromBase.getPageParams)), this.store.pipe(select(fromCatalog.getProductSortOrder)), function (action, state, pageNumber, params, sortOrder) { return ({ state: state, pageNumber: pageNumber, params: params, sortOrder: sortOrder }); }), 
        // distinct(combined => combined.state.valueKey, this.flushDistinctExpand$),
        switchMap(function (combined) {
            return _this.productSvc
                .findProducts(combined.state.customerId, combined.state.pageId, combined.state.facets, combined.state.onlyInStock, combined.pageNumber.toString(), combined.params, combined.state.searchTerm, null, null, combined.state.filterItems, combined.sortOrder)
                .pipe(map(function (products) { return new product.ExpandSuccess(products); }), catchError(function (e) { return of(new product.ExpandFailed(e)); }));
        }));
        /**
         * Product Add Effect
         */
        this.addProduct$ = this.actions$.ofType(product.ADD)
            .pipe(toPayload(), withLatestFrom(this.store.pipe(select(fromAuth.getCustomerId)), function (productId, customerId) { return ({ productId: productId, customerId: customerId }); }), switchMap(function (data) {
            return _this.productSvc.findProduct(data.productId, data.customerId)
                .pipe(switchMap(function (result) { return [new product.AddSuccess(result), new product.Select(result)]; }), catchError(function (e) { return of(new product.AddFailed(e)); }));
        }));
        /**
         * Product Selection Effects
         */
        this.addWhenNotLoaded$ = this.actions$.ofType(product.SELECT)
            .pipe(toPayload(), withLatestFrom(this.store.pipe(select(fromCatalog.getProductIds))), filter(function (_a) {
            var item = _a[0], ids = _a[1];
            if (typeof item === 'string')
                return ids.indexOf(item) === -1;
            else
                return ids.indexOf(item && item._id) === -1;
        }), map(function (_a) {
            var item = _a[0], ids = _a[1];
            if (typeof item === 'string')
                return new product.Add(item);
            else
                return new product.Add(item && item._id);
        }));
        /**
         * Favorites effects
         */
        this.getFavoritesIds$ = this.actions$.ofType(product.GET_FAVORITES)
            .pipe(withLatestFrom(this.store.pipe(select(fromAuth.getCustomerId)), function (action, customerId) { return customerId; }), switchMap(function (customerId) {
            return _this.productSvc.getFavoritesIds(customerId)
                .pipe(map(function (favorites) { return new GetFavoritesSuccess(favorites); }), catchError(function (e) { return of(new product.GetFavoritesFailed(e)); }));
        }));
        this.addFavorite$ = this.actions$.ofType(product.ADD_FAVORITE)
            .pipe(toPayload(), withLatestFrom(this.store.pipe(select(fromAuth.getCustomerId)), function (product, customerId) { return ({ product: product, customerId: customerId }); }), switchMap(function (data) {
            return _this.productSvc.addFavorite(data.customerId, data.product.code)
                .pipe(map(function (orderItem) { return new product.AddFavoriteSuccess(orderItem); }), catchError(function (error) { return of(new product.AddFavoriteFailed(error)); }));
        }));
        this.removeFavorite$ = this.actions$.ofType(product.REMOVE_FAVORITE)
            .pipe(toPayload(), withLatestFrom(this.store.pipe(select(fromCatalog.getFavorites)), this.store.pipe(select(fromBase.getPageParams)), function (product, favorites, params) {
            return {
                product: product,
                params: params,
                favoriteItem: favorites.find(function (item) { return item.productCode === product.code; })
            };
        }), switchMap(function (data) {
            return _this.productSvc.removeFavorite(data.favoriteItem._id)
                .pipe(switchMap(function (res) {
                if (data.params) {
                    return [
                        new product.RemoveFavoriteSuccess(data.product.code),
                        new product.Remove(data.product._id),
                        new facet.Filter()
                    ];
                }
                else {
                    return [
                        new product.RemoveFavoriteSuccess(data.product.code)
                    ];
                }
            }), catchError(function (error) { return of(new product.RemoveFavoriteFailed(error)); }));
        }));
        this.getProductOrderHistory$ = this.actions$
            .pipe(ofType(product.SELECT), toPayload(), withPredicateFrom(this.store.pipe(select(fromAuth.getCustomerId)), function (customerId) { return !!customerId; }), withLatestFrom(this.store.pipe(select(fromAuth.getCustomerId)), function (product, customerId) { return ({ product: product, customerId: customerId }); }), switchMap(function (data) {
            return _this.productSvc.getProductOrderHistory(data.customerId, data.product && data.product.code)
                .pipe(map(function (response) { return new product.GetProductHistorySuccess(chaining(response, 'results')); }), catchError(function (error) { return of(new product.GetProductHistoryFailed(error)); }));
        }));
    }
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "loadWhenInStockToggled$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "filterProductsOnSetProductFilter$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "setDefaultCatalogSortOrder$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "sortProductsOnSetProductSortOrder$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "filterProducts$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "expand$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "addProduct$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "addWhenNotLoaded$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "getFavoritesIds$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "addFavorite$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "removeFavorite$", void 0);
    tslib_1.__decorate([
        Effect(),
        tslib_1.__metadata("design:type", Observable)
    ], ProductEffects.prototype, "getProductOrderHistory$", void 0);
    return ProductEffects;
}());
export { ProductEffects };
